import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ValidationTree from "../components/ValidationTree";
import { ArrowLeft, IndianRupee, Package, AlertTriangle, Sparkles, FileDown, Mail, Loader2, Check, Edit3, Save, X, Clock } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";
import SignalCard from "../components/SignalCard";
import { OpportunityConceptMemo } from "../components/OpportunityConceptMemo";

function MemoSection({ label, children, testId }) {
  return (
    <section className="border-t border-white/10 pt-4" data-testid={testId}>
      <div className="label-mono text-bm-amber mb-2">{label}</div>
      {children}
    </section>
  );
}

function Pill({ children, color = "border-white/15 text-bm-muted" }) {
  return <span className={`text-xs font-mono px-2 py-1 border ${color}`}>{children}</span>;
}

export default function OpportunityDetail() {
  const { id } = useParams();
  const [opp, setOpp] = useState(null);
  const [emailing, setEmailing] = useState(false);
  const [emailResult, setEmailResult] = useState(null);
  const [emailConfigured, setEmailConfigured] = useState(null);
  const [editing, setEditing] = useState(false);
  const [savingPatch, setSavingPatch] = useState(false);
  const [patchDraft, setPatchDraft] = useState({});

  useEffect(() => {
    api.opportunity(id).then((d) => {
      setOpp(d);
      setPatchDraft({
        subcategory: d.subcategory || "",
        ux_label: d.ux_label || "Validated",
        product_nomenclature: d.product_nomenclature || d.product_name || "",
        signal_type: d.signal_type || "",
      });
    });
    api.emailStatus().then((s) => setEmailConfigured(s.configured));
  }, [id]);

  useEffect(() => {
    const active = ["queued", "processing"].includes(opp?.memo_status)
      || ["queued", "processing"].includes(opp?.packshot_status);
    if (!active) return undefined;
    const timer = window.setInterval(() => {
      api.opportunity(id).then((data) => setOpp(data)).catch(() => {});
    }, 4000);
    return () => window.clearInterval(timer);
  }, [id, opp?.memo_status, opp?.packshot_status]);

  const savePatch = async () => {
    setSavingPatch(true);
    try {
      const r = await api.patchOpportunity(id, patchDraft);
      setOpp(r.opportunity);
      setEditing(false);
    } finally {
      setSavingPatch(false);
    }
  };

  const sendEmail = async () => {
    setEmailing(true);
    setEmailResult(null);
    try {
      const r = await api.emailMemo(id);
      setEmailResult(r);
    } catch (e) {
      setEmailResult({ status: "error", message: e.message });
    } finally {
      setEmailing(false);
    }
  };

  if (!opp) return <div className="font-mono text-xs text-bm-cyan scanline p-6">LOADING MEMO...</div>;

  return (
    <div className="space-y-6 max-w-6xl mx-auto" data-testid="opp-detail">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Link to="/opportunities" className="inline-flex items-center gap-2 label-mono text-bm-muted hover:text-white" data-testid="back-link">
          <ArrowLeft className="w-3 h-3" />BACK TO PIPELINE
        </Link>
        <div className="flex items-center gap-2">
          <a
            href={api.pdfUrl(id)}
            target="_blank"
            rel="noreferrer"
            data-testid="export-pdf-btn"
            className="flex items-center gap-2 px-3 h-9 text-xs font-mono border border-bm-cyan/50 text-bm-cyan hover:bg-bm-cyan hover:text-black transition-colors"
          >
            <FileDown className="w-3.5 h-3.5" />EXPORT PDF
          </a>
          <button
            onClick={sendEmail}
            disabled={emailing}
            data-testid="email-memo-btn"
            className="flex items-center gap-2 px-3 h-9 text-xs font-mono bg-bm-amber text-black hover:bg-white transition-colors disabled:opacity-50"
          >
            {emailing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
            EMAIL TO ISHITA
          </button>
        </div>
      </div>

      {emailResult && (
        <div
          data-testid="email-result"
          className={`bm-card p-3 text-xs font-mono flex items-start gap-2 ${
            emailResult.status === "sent" ? "border-l-2 border-bm-green" :
            emailResult.status === "skipped" ? "border-l-2 border-bm-amber" :
            "border-l-2 border-bm-danger"
          }`}
        >
          {emailResult.status === "sent" ? <Check className="w-4 h-4 text-bm-green shrink-0" /> :
           emailResult.status === "skipped" ? <AlertTriangle className="w-4 h-4 text-bm-amber shrink-0" /> :
           <AlertTriangle className="w-4 h-4 text-bm-danger shrink-0" />}
          <span className="text-bm-muted">{emailResult.message}</span>
        </div>
      )}

      {emailConfigured === false && !emailResult && (
        <div className="bm-card p-3 text-xs font-mono border-l-2 border-bm-amber flex items-start gap-2" data-testid="email-not-configured">
          <AlertTriangle className="w-4 h-4 text-bm-amber shrink-0" />
          <span className="text-bm-muted">
            Email delivery via Resend is <span className="text-bm-amber">not configured</span>. PDF export works. Add <code className="text-bm-cyan">RESEND_API_KEY</code> to <code className="text-bm-cyan">/app/backend/.env</code> to enable email-to-committee.
          </span>
        </div>
      )}

      {/* Header memo block */}
      <div className="bm-card p-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="label-mono text-bm-cyan">{opp.category}</span>
            {opp.subcategory && <span className="label-mono text-bm-amber">· {opp.subcategory}</span>}
            {opp.signal_type && <span className="label-mono text-bm-dim">· {opp.signal_type}</span>}
            <span className="label-mono text-bm-dim">ORIGIN / {opp.origin_geography}</span>
            <Pill color="border-bm-green/40 text-bm-green">STATUS / {opp.status.toUpperCase()}</Pill>
            {opp.ux_label && (
              <span className={`label-mono px-1.5 py-0.5 border ${
                opp.ux_label === "Whitespace" ? "border-bm-cyan/50 text-bm-cyan" :
                opp.ux_label === "Emerging" ? "border-bm-amber/50 text-bm-amber" :
                opp.ux_label === "Validated" ? "border-bm-green/50 text-bm-green" :
                "border-bm-danger/50 text-bm-danger"
              }`} data-testid="opp-ux-label">
                {opp.ux_label.toUpperCase()}
              </span>
            )}
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight" data-testid="opp-title">
            {opp.product_nomenclature || opp.product_name}
          </h1>
          {opp.product_nomenclature && opp.product_nomenclature !== opp.product_name && (
            <div className="label-mono text-bm-dim">CODENAME / {opp.product_name}</div>
          )}
          <p className="text-base text-bm-muted">{opp.tagline}</p>
          <div className="flex flex-wrap gap-3 pt-2 text-sm font-mono">
            <span className="flex items-center gap-1.5"><IndianRupee className="w-3.5 h-3.5 text-bm-amber" />{opp.price_inr}</span>
            <span className="flex items-center gap-1.5"><Package className="w-3.5 h-3.5 text-bm-amber" />{opp.pack_size}</span>
            <span className="flex items-center gap-1.5 text-bm-dim" data-testid="opp-generated-at">
              <Clock className="w-3.5 h-3.5" />Generated {formatGeneratedAt(opp.generated_at || opp.created_at)}
            </span>
          </div>
        </div>

        <div className="bm-card-elev p-4 space-y-3">
          <div className="label-mono text-bm-dim">OVERALL CONVICTION</div>
          <div className="font-heading font-black text-6xl text-bm-green leading-none" data-testid="overall-conviction">{opp.scores.overall}<span className="text-2xl text-bm-muted">%</span></div>
          <div className="space-y-1.5 pt-2 border-t border-white/5">
            {[
              ["Category", opp.scores.category],
              ["Behaviour", opp.scores.behaviour],
              ["Product", opp.scores.product],
              ["Localization", opp.scores.localization],
              ["Commercial", opp.scores.commercial],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between text-xs font-mono">
                <span className="text-bm-muted">{k}</span>
                <span className={v >= 80 ? "text-bm-green" : v >= 65 ? "text-bm-amber" : "text-bm-danger"}>{v}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {opp.source_type === "insight_promotion" && opp.source_insight && (
        <section className="border border-bm-cyan/40 bg-bm-surface p-5 space-y-4" data-testid="opportunity-source-provenance">
          <div className="flex items-start justify-between gap-4 flex-wrap border-b border-white/10 pb-3">
            <div>
              <div className="label-mono text-bm-cyan">EXACT SOURCE PROVENANCE</div>
              <Link to={`/insights/${opp.source_insight.id}`} className="font-heading text-xl font-bold hover:text-bm-cyan transition-colors" data-testid="source-insight-link">
                {opp.source_insight.name}
              </Link>
            </div>
            <div className="text-right font-mono text-xs">
              <div className="text-bm-green" data-testid="source-signal-count">{opp.source_signals.length} VERIFIED SIGNAL{opp.source_signals.length === 1 ? "" : "S"}</div>
              <div className="text-bm-dim mt-1">SCORE METHOD / {opp.score_method?.toUpperCase()}</div>
            </div>
          </div>

          {Object.keys(opp.promotion_inputs || {}).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="opportunity-promotion-inputs">
              {Object.entries(opp.promotion_inputs).map(([key, value]) => (
                <div key={key} className="border-t border-white/10 pt-2 min-w-0">
                  <div className="label-mono text-bm-dim">{key.replaceAll("_", " ")}</div>
                  <div className="text-xs font-mono text-bm-muted mt-1 break-words">{value}</div>
                </div>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="opportunity-source-signals">
            {opp.source_signals.map((signal, index) => (
              <Link key={signal.id} to={`/signals?title=${encodeURIComponent(signal.title)}`} data-testid={`opportunity-source-signal-${signal.id}`}
                className="block focus-visible:outline focus-visible:outline-2 focus-visible:outline-bm-cyan">
                <SignalCard signal={signal} idx={index} expanded />
              </Link>
            ))}
          </div>
        </section>
      )}

      <OpportunityConceptMemo opportunity={opp} onUpdate={setOpp} />

      {/* TAXONOMY · editable inline */}
      <div className="bm-card p-5" data-testid="opp-taxonomy">
        <div className="flex items-center justify-between mb-3 pb-2 border-b border-white/5">
          <span className="label-mono text-bm-amber">TAXONOMY · EDITABLE</span>
          {!editing ? (
            <button onClick={() => setEditing(true)} data-testid="taxonomy-edit-btn"
              className="flex items-center gap-1.5 px-2 py-1 text-xs font-mono text-bm-cyan border border-bm-cyan/30 hover:bg-bm-cyan hover:text-black transition-colors">
              <Edit3 className="w-3 h-3" />EDIT
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(false)} data-testid="taxonomy-cancel-btn"
                className="flex items-center gap-1.5 px-2 py-1 text-xs font-mono text-bm-muted border border-white/10 hover:text-white">
                <X className="w-3 h-3" />CANCEL
              </button>
              <button onClick={savePatch} disabled={savingPatch} data-testid="taxonomy-save-btn"
                className="flex items-center gap-1.5 px-2 py-1 text-xs font-mono bg-bm-green text-black hover:bg-white disabled:opacity-50">
                {savingPatch ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}SAVE
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            { key: "subcategory", label: "Subcategory", placeholder: "e.g. Gourmand, SPF, Scalp Care" },
            { key: "product_nomenclature", label: "Product Nomenclature", placeholder: "e.g. Drinkable SPF Mist" },
            { key: "signal_type", label: "Signal Type", placeholder: "e.g. Whitespace Opportunity" },
          ].map((f) => (
            <div key={f.key}>
              <div className="label-mono text-bm-dim mb-1">{f.label.toUpperCase()}</div>
              {editing ? (
                <input
                  value={patchDraft[f.key] || ""}
                  onChange={(e) => setPatchDraft({ ...patchDraft, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  data-testid={`taxonomy-input-${f.key}`}
                  className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
                />
              ) : (
                <div className="font-mono text-sm text-white" data-testid={`taxonomy-${f.key}`}>{opp[f.key] || "—"}</div>
              )}
            </div>
          ))}
          <div>
            <div className="label-mono text-bm-dim mb-1">UX LABEL</div>
            {editing ? (
              <select
                value={patchDraft.ux_label || "Validated"}
                onChange={(e) => setPatchDraft({ ...patchDraft, ux_label: e.target.value })}
                data-testid="taxonomy-input-ux_label"
                className="w-full bg-bm-elevated border border-white/10 px-3 py-2 font-mono text-sm text-white outline-none focus:border-bm-cyan"
              >
                {["Whitespace", "Emerging", "Validated", "Saturated"].map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <div className="font-mono text-sm text-white" data-testid="taxonomy-ux_label">{opp.ux_label || "—"}</div>
            )}
          </div>
        </div>
      </div>

      <MemoSection label="WHY NOW" testId="section-why-now">
        <p className="text-base text-bm-text/90 leading-relaxed">{opp.why_now}</p>
      </MemoSection>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MemoSection label="SUPPORTING CATEGORY TRENDS" testId="section-cat-trends">
          <ul className="space-y-1.5 text-sm text-bm-muted">
            {opp.supporting_category_trends.map((t, i) => <li key={`cat-${i}-${t.slice(0,16)}`} className="flex gap-2"><span className="text-bm-cyan">→</span>{t}</li>)}
          </ul>
        </MemoSection>
        <MemoSection label="SUPPORTING BEHAVIOUR SHIFTS" testId="section-behav">
          <ul className="space-y-1.5 text-sm text-bm-muted">
            {opp.supporting_behaviour_shifts.map((t, i) => <li key={`beh-${i}-${t.slice(0,16)}`} className="flex gap-2"><span className="text-bm-amber">→</span>{t}</li>)}
          </ul>
        </MemoSection>
        <MemoSection label="PRODUCT VALIDATION" testId="section-prod-val">
          <ul className="space-y-1.5 text-sm text-bm-muted">
            {opp.supporting_product_validation.map((t, i) => <li key={`pv-${i}-${t.slice(0,16)}`} className="flex gap-2"><span className="text-bm-green">→</span>{t}</li>)}
          </ul>
        </MemoSection>
      </div>

      <MemoSection label="INDIA ADAPTATION RATIONALE" testId="section-india">
        <p className="text-base text-bm-text/90 leading-relaxed">{opp.india_adaptation_rationale}</p>
      </MemoSection>

      <MemoSection label="VALIDATION TREES" testId="section-trees">
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {opp.validation_trees.map((t) => <ValidationTree key={t.tree_type} tree={t} />)}
        </div>
      </MemoSection>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <MemoSection label="GTM PLAYBOOK" testId="section-gtm">
          <div className="flex flex-wrap gap-2">
            {opp.suggested_gtm.map((g, i) => <Pill key={`gtm-${i}-${g.slice(0,16)}`} color="border-bm-cyan/40 text-bm-cyan">{g}</Pill>)}
          </div>
        </MemoSection>
        <MemoSection label="CREATOR ARCHETYPES" testId="section-creators">
          <div className="flex flex-wrap gap-2">
            {opp.suggested_creator_archetypes.map((g, i) => <Pill key={`arc-${i}-${g.slice(0,16)}`} color="border-bm-amber/40 text-bm-amber">{g}</Pill>)}
          </div>
        </MemoSection>
        <MemoSection label="MARKETPLACES" testId="section-marketplaces">
          <div className="flex flex-wrap gap-2">
            {opp.suggested_marketplaces.map((g, i) => <Pill key={`mp-${i}-${g.slice(0,16)}`} color="border-bm-green/40 text-bm-green">{g}</Pill>)}
          </div>
        </MemoSection>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MemoSection label="RISKS" testId="section-risks">
          <ul className="space-y-1.5 text-sm text-bm-muted">
            {opp.risks.map((r, i) => (
              <li key={`risk-${i}-${r.slice(0,16)}`} className="flex gap-2 items-start"><AlertTriangle className="w-3.5 h-3.5 text-bm-danger mt-0.5 shrink-0" />{r}</li>
            ))}
          </ul>
        </MemoSection>
        <MemoSection label="COMPARABLES" testId="section-comparables">
          <ul className="space-y-1.5 text-sm text-bm-muted">
            {opp.comparables.map((c, i) => <li key={`cmp-${i}-${c.slice(0,16)}`} className="flex gap-2"><Sparkles className="w-3.5 h-3.5 text-bm-cyan mt-0.5 shrink-0" />{c}</li>)}
          </ul>
        </MemoSection>
      </div>
    </div>
  );
}
